import React, { useState } from "react";
import { Workflow } from "../../../../../services/WorkflowService";
import { Button } from "../../../../ui/button";
import { PlusIcon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  STRUCTURED_PROMPT_SECTION,
  STRUCTURED_PROMPT_SECTION_DESCRIPTION,
  STRUCTURED_PROMPT_SECTION_TITLE,
} from "./promptCardSection";
import {
  GenerationConfig,
  GenerationVariable,
  computedPlaceholdersFromVariables,
  mergePlaceholdersWithGenerationVars,
  mergeWorkflowInputMissingVariableKeys,
} from "./types";
import { EditVariable, variableDraftIsValid } from "./EditVariable";
import { PromptVariable } from "./PromptVariable";

function emptyGenerationVariable(): GenerationVariable {
  return {
    name: "",
    description: "",
    example: "",
    source: "seed",
    workflow_ref: "",
    arg_bindings: [],
    value_path: "",
  };
}

function cloneVariable(v: GenerationVariable): GenerationVariable {
  return {
    name: v.name,
    description: v.description,
    example: v.example || "",
    source: v.source,
    workflow_ref: v.workflow_ref,
    arg_bindings: (v.arg_bindings || []).map((b) => ({ ...b })),
    value_path: v.value_path,
  };
}

function savedVariableNames(list: GenerationVariable[]): string[] {
  const out: string[] = [];
  for (const v of list) {
    const n = v.name.trim();
    if (n && /^\w+$/.test(n)) out.push(n);
  }
  return out;
}

function priorNamesBeforeIndex(saved: GenerationVariable[], index: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < index; i += 1) {
    const n = saved[i].name.trim();
    if (n && /^\w+$/.test(n)) out.push(n);
  }
  return out;
}

type EditorSession =
  | { kind: "idle" }
  | { kind: "create"; draft: GenerationVariable }
  | { kind: "edit"; index: number; draft: GenerationVariable };

export interface PromptVariablesEditorProps {
  variables: GenerationVariable[];
  onVariablesChange: (next: GenerationVariable[]) => void;
  workflows: Workflow[];
}

export function PromptVariablesEditor({
  variables,
  onVariablesChange,
  workflows,
}: PromptVariablesEditorProps) {
  const [session, setSession] = useState<EditorSession>({ kind: "idle" });

  const isBusy = session.kind !== "idle";

  const commitList = (next: GenerationVariable[]) => {
    onVariablesChange(next);
  };

  const moveRow = (index: number, delta: number) => {
    if (isBusy) return;
    const j = index + delta;
    if (j < 0 || j >= variables.length) return;
    const next = [...variables];
    const tmp = next[index];
    next[index] = next[j];
    next[j] = tmp;
    commitList(next);
  };

  const removeRow = (index: number) => {
    if (session.kind === "edit" && session.index === index) {
      setSession({ kind: "idle" });
    }
    commitList(variables.filter((_, i) => i !== index));
  };

  const startCreate = () => {
    setSession({ kind: "create", draft: emptyGenerationVariable() });
  };

  const startEdit = (index: number) => {
    setSession({ kind: "edit", index, draft: cloneVariable(variables[index]) });
  };

  const cancelSession = () => {
    setSession({ kind: "idle" });
  };

  const saveCreate = () => {
    if (session.kind !== "create") return;
    if (!variableDraftIsValid(session.draft)) return;
    commitList([...variables, session.draft]);
    setSession({ kind: "idle" });
  };

  const saveEdit = () => {
    if (session.kind !== "edit") return;
    if (!variableDraftIsValid(session.draft)) return;
    const next = variables.map((row, i) => (i === session.index ? session.draft : row));
    commitList(next);
    setSession({ kind: "idle" });
  };

  const updateCreateDraft = (patch: Partial<GenerationVariable>) => {
    if (session.kind !== "create") return;
    setSession({ kind: "create", draft: { ...session.draft, ...patch } });
  };

  const updateEditDraft = (patch: Partial<GenerationVariable>) => {
    if (session.kind !== "edit") return;
    setSession({ kind: "edit", index: session.index, draft: { ...session.draft, ...patch } });
  };

  const priorForCreate = savedVariableNames(variables);
  const priorForEdit =
    session.kind === "edit" ? priorNamesBeforeIndex(variables, session.index) : [];

  return (
    <div className={STRUCTURED_PROMPT_SECTION}>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <div className={STRUCTURED_PROMPT_SECTION_TITLE}>Variables</div>
          <p className={STRUCTURED_PROMPT_SECTION_DESCRIPTION}>
            Define placeholders in order (top to bottom). Computed variables may use only variables above them as workflow args. Use{" "}
            <code className="text-[11px] bg-background px-1 py-px rounded border border-border">{"{{name}}"}</code> in the user message template.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={startCreate} className="shrink-0" disabled={isBusy}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add variable
        </Button>
      </div>

      {variables.length === 0 && session.kind === "idle" ? (
        <p className={cn(STRUCTURED_PROMPT_SECTION_DESCRIPTION, "italic")}>No variables yet — add one or type placeholders directly in the template (legacy).</p>
      ) : (
        <div className="space-y-2">
          {variables.map((row, index) => {
            if (session.kind === "edit" && session.index === index) {
              return (
                <EditVariable
                  key={`edit-${index}`}
                  draft={session.draft}
                  onDraftChange={updateEditDraft}
                  priorNames={priorForEdit}
                  workflows={workflows}
                  title="Edit variable"
                  onSave={saveEdit}
                  onCancel={cancelSession}
                  saveDisabled={!variableDraftIsValid(session.draft)}
                />
              );
            }
            return (
              <PromptVariable
                key={`saved-${index}-${row.name}`}
                variable={row}
                index={index}
                workflows={workflows}
                disableUp={index === 0 || isBusy}
                disableDown={index === variables.length - 1 || isBusy}
                onEdit={() => startEdit(index)}
                onRemove={() => removeRow(index)}
                onMoveUp={() => moveRow(index, -1)}
                onMoveDown={() => moveRow(index, 1)}
              />
            );
          })}
        </div>
      )}

      {session.kind === "create" && (
        <EditVariable
          draft={session.draft}
          onDraftChange={updateCreateDraft}
          priorNames={priorForCreate}
          workflows={workflows}
          title="New variable"
          onSave={saveCreate}
          onCancel={cancelSession}
          saveDisabled={!variableDraftIsValid(session.draft)}
        />
      )}
    </div>
  );
}

export function applyVariablesToConfig(
  next: GenerationVariable[],
  onChange: (partial: Partial<GenerationConfig>) => void,
  workflowInputContext?: Pick<GenerationConfig, "generation_teacher_type" | "generation_workflow_input"> & {
    workflowInputSchemaKeys?: string[];
  }
) {
  const computed = computedPlaceholdersFromVariables(next);
  const partial: Partial<GenerationConfig> = {
    generation_variables: next,
    generation_computed_placeholders: computed,
  };
  if (workflowInputContext?.generation_teacher_type === "workflow") {
    const ph = mergePlaceholdersWithGenerationVars("", "", next, computed);
    const schemaKeys = workflowInputContext.workflowInputSchemaKeys;
    partial.generation_workflow_input = mergeWorkflowInputMissingVariableKeys(
      workflowInputContext.generation_workflow_input,
      ph,
      schemaKeys
    );
  }
  onChange(partial);
}
